/**
 * Patients — a clinical record.
 *
 * Readable only with `patients:read`, which no cashier holds. Allergies are
 * shown before anything else on the record, because they are the one fact a
 * pharmacist must not miss. Reminder consent is recorded here, and sending
 * stays off until a verified messaging channel exists: consent given is not a
 * message sent.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, HeartPulse, Lock, Plus, X } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';
import { Button, Card, CardHeader, EmptyState, ErrorState, Input, Modal, Select, Spinner, Textarea } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyWrite, type ListOf } from './data';
import { ErrorLine, StatusBadge, Table, Td } from './parts';

interface PatientRow {
  id: string;
  full_name: string;
  phone: string | null;
  date_of_birth: string | null;
  has_allergies: boolean;
}

export interface PatientRecord {
  id: string;
  customerId: string | null;
  fullName: string;
  dateOfBirth: string | null;
  sex: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  allergies: string[];
  clinicalNotes: string | null;
  reminderConsent: boolean;
  reminderConsentAt: string | null;
}

interface HistoryRow {
  id: string;
  dispensed_at: string;
  product_name: string;
  base_quantity: number;
  label_directions: string | null;
  dispensed_by_name: string;
  prescription_reference: string;
  branch_name: string | null;
}

export function ClinicalNotice(): ReactNode {
  return (
    <p className="mb-4 inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
      <Lock className="size-3.5" aria-hidden /> Clinical record — visible only to authorised staff
    </p>
  );
}

export function PatientsPage(): ReactNode {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const list = usePharmacy<ListOf<PatientRow>>('/patients', { search }, can('patients:read'));

  if (!can('patients:read')) {
    return <EmptyState title="Patients are a clinical record" description="Ask the owner for access if your role needs it." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Patients"
        description="Contact details, allergies, notes and medication history"
        action={
          can('patients:manage') ? (
            <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              New patient
            </Button>
          ) : undefined
        }
      />
      <ClinicalNotice />
      <div className="max-w-md">
        <Input label="Search by name or phone" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Start typing…" />
      </div>
      <Card padded={false}>
        {list.isError ? (
          <ErrorState message="Could not load patients." onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Spinner label="Loading patients" />
        ) : list.data.data.length === 0 ? (
          <EmptyState icon={<HeartPulse className="size-8" />} title="No patients found" description={search ? 'Try another name or number.' : 'Patients you record appear here.'} />
        ) : (
          <Table head={['Name', 'Phone', 'Date of birth', 'Allergies']} minWidth={520}>
            {list.data.data.map((p) => (
              <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/pharmacy/patients/${p.id}`)}>
                <Td>
                  <Link to={`/pharmacy/patients/${p.id}`} className="font-medium text-brand-700 hover:underline">
                    {p.full_name}
                  </Link>
                </Td>
                <Td>{p.phone ?? '—'}</Td>
                <Td>{p.date_of_birth ?? '—'}</Td>
                <Td>{p.has_allergies ? <span className="font-medium text-red-700">Recorded</span> : <span className="text-slate-500">None recorded</span>}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <PatientFormModal
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={(p) => {
          setCreating(false);
          navigate(`/pharmacy/patients/${p.id}`);
        }}
      />
    </div>
  );
}

export function PatientFormModal({
  open,
  onClose,
  onSaved,
  existing,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: (p: PatientRecord) => void;
  existing?: PatientRecord;
}): ReactNode {
  const create = usePharmacyWrite<Record<string, unknown>, PatientRecord>('post', '/patients');
  const update = usePharmacyWrite<Record<string, unknown>, PatientRecord>('patch', `/patients/${existing?.id ?? ''}`);
  const write = existing ? update : create;
  const [form, setForm] = useState(() => blank(existing));
  const [allergy, setAllergy] = useState('');

  useEffect(() => {
    if (open) setForm(blank(existing));
  }, [open, existing]);

  function addAllergy() {
    const value = allergy.trim();
    if (value && !form.allergies.includes(value)) setForm({ ...form, allergies: [...form.allergies, value] });
    setAllergy('');
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const body = {
      fullName: form.fullName,
      dateOfBirth: form.dateOfBirth || null,
      sex: form.sex || null,
      phone: form.phone || null,
      email: form.email || null,
      address: form.address || null,
      allergies: form.allergies,
      clinicalNotes: form.clinicalNotes || null,
      reminderConsent: form.reminderConsent,
    };
    write.mutate(body, { onSuccess: onSaved });
  }

  return (
    <Modal open={open} onClose={onClose} title={existing ? 'Edit patient' : 'New patient'} size="lg">
      <form onSubmit={submit} className="space-y-4">
        <ClinicalNotice />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Full name" required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          <Input label="Date of birth" type="date" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
          <Select
            label="Sex"
            value={form.sex}
            onChange={(e) => setForm({ ...form, sex: e.target.value })}
            placeholder="Not recorded"
            options={[
              { value: 'female', label: 'Female' },
              { value: 'male', label: 'Male' },
              { value: 'other', label: 'Other' },
              { value: 'unknown', label: 'Unknown' },
            ]}
          />
          <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>

        <div>
          <p className="label-base">Allergies</p>
          <div className="flex flex-wrap gap-1.5">
            {form.allergies.map((a) => (
              <span key={a} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200">
                {a}
                <button type="button" aria-label={`Remove ${a}`} onClick={() => setForm({ ...form, allergies: form.allergies.filter((x) => x !== a) })}>
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <Input
              aria-label="Add an allergy"
              placeholder="e.g. Penicillin"
              value={allergy}
              onChange={(e) => setAllergy(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addAllergy();
                }
              }}
            />
            <Button type="button" variant="secondary" onClick={addAllergy}>
              Add
            </Button>
          </div>
        </div>

        <Textarea label="Clinical notes" value={form.clinicalNotes} onChange={(e) => setForm({ ...form, clinicalNotes: e.target.value })} />

        <label className="flex items-start gap-2 rounded-lg border border-slate-200 p-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.reminderConsent}
            onChange={(e) => setForm({ ...form, reminderConsent: e.target.checked })}
          />
          <span>
            <span className="font-medium text-slate-900">Patient consents to refill reminders</span>
            <span className="block text-xs text-slate-500">
              Recorded with who and when. No reminder is sent until a verified SMS or WhatsApp channel is set up for this
              pharmacy — the consent is kept so it is ready, not acted on.
            </span>
          </span>
        </label>

        <ErrorLine error={write.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={write.isPending}>
            {existing ? 'Save changes' : 'Create patient'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function blank(p?: PatientRecord) {
  return {
    fullName: p?.fullName ?? '',
    dateOfBirth: p?.dateOfBirth ?? '',
    sex: p?.sex ?? '',
    phone: p?.phone ?? '',
    email: p?.email ?? '',
    address: p?.address ?? '',
    allergies: p?.allergies ?? [],
    clinicalNotes: p?.clinicalNotes ?? '',
    reminderConsent: p?.reminderConsent ?? false,
  };
}

export function PatientDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const { can, business } = useAuth();
  const tz = business?.timezone ?? 'UTC';
  const [editing, setEditing] = useState(false);
  const record = usePharmacy<PatientRecord & { medicationHistory: HistoryRow[] }>(`/patients/${id}`, {}, can('patients:read'));
  const p = record.data;

  return (
    <div className="space-y-5">
      <Link to="/pharmacy/patients" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Patients
      </Link>
      {record.isError ? (
        <ErrorState message="Could not load this patient." onRetry={() => void record.refetch()} />
      ) : !p ? (
        <Spinner label="Loading patient" />
      ) : (
        <>
          <PageHeader
            title={p.fullName}
            description={[p.phone, p.dateOfBirth ? `Born ${p.dateOfBirth}` : null].filter(Boolean).join(' · ') || undefined}
            action={
              can('patients:manage') ? (
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              ) : undefined
            }
          />
          <ClinicalNotice />

          <AllergyBanner allergies={p.allergies} />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader title="Details" />
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <Item label="Sex" value={p.sex} />
                <Item label="Email" value={p.email} />
                <Item label="Address" value={p.address} />
                <Item label="Clinical notes" value={p.clinicalNotes} />
              </dl>
            </Card>
            <Card>
              <CardHeader title="Refill reminders" />
              {p.reminderConsent ? (
                <p className="text-sm text-slate-700">
                  Consent given {p.reminderConsentAt ? dateTime(p.reminderConsentAt, tz) : ''}.
                </p>
              ) : (
                <p className="text-sm text-slate-700">No consent recorded.</p>
              )}
              <p className="mt-2 text-xs text-slate-500">Nothing is sent until a verified messaging channel is configured.</p>
            </Card>
          </div>

          <Card padded={false}>
            <div className="p-4 sm:p-5">
              <CardHeader
                title="Medication history"
                subtitle="Everything dispensed against this patient's prescriptions"
                action={
                  <Link to="/pharmacy/prescriptions" className="text-sm text-brand-700 hover:underline">
                    All prescriptions
                  </Link>
                }
              />
            </div>
            {p.medicationHistory.length === 0 ? (
              <p className="px-5 pb-6 text-sm text-slate-500">Nothing dispensed yet.</p>
            ) : (
              <Table head={['Date', 'Medicine', 'Quantity', 'Directions', 'Prescription', 'Dispensed by']} minWidth={720}>
                {p.medicationHistory.map((h) => (
                  <tr key={h.id}>
                    <Td className="whitespace-nowrap">{dateTime(h.dispensed_at, tz)}</Td>
                    <Td className="font-medium">{h.product_name}</Td>
                    <Td className="tabular-nums">{h.base_quantity}</Td>
                    <Td className="max-w-[16rem]">{h.label_directions ?? '—'}</Td>
                    <Td>{h.prescription_reference}</Td>
                    <Td>{h.dispensed_by_name}</Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>

          {can('prescriptions:read') && <PatientPrescriptions patientId={p.id} timezone={tz} />}

          <PatientFormModal open={editing} existing={p} onClose={() => setEditing(false)} onSaved={() => setEditing(false)} />
        </>
      )}
    </div>
  );
}

interface PatientPrescriptionRow {
  id: string;
  reference: string;
  received_at: string;
  dispensing_status: string;
  branch_name: string | null;
}

function PatientPrescriptions({ patientId, timezone }: { patientId: string; timezone: string }): ReactNode {
  const list = usePharmacy<ListOf<PatientPrescriptionRow>>('/prescriptions', { patientId });
  return (
    <Card padded={false}>
      <div className="p-4 sm:p-5">
        <CardHeader title="Prescriptions" subtitle="Every prescription recorded for this patient" />
      </div>
      {list.isError ? (
        <ErrorState message="Could not load prescriptions." onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <Spinner label="Loading prescriptions" />
      ) : list.data.data.length === 0 ? (
        <p className="px-5 pb-6 text-sm text-slate-500">No prescriptions yet.</p>
      ) : (
        <Table head={['Reference', 'Received', 'Status', 'Location']} minWidth={520}>
          {list.data.data.map((rx) => (
            <tr key={rx.id}>
              <Td>
                <Link to={`/pharmacy/prescriptions/${rx.id}`} className="font-medium text-brand-700 hover:underline">
                  {rx.reference}
                </Link>
              </Td>
              <Td className="whitespace-nowrap">{dateTime(rx.received_at, timezone)}</Td>
              <Td><StatusBadge status={rx.dispensing_status} /></Td>
              <Td>{rx.branch_name ?? '—'}</Td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

export function AllergyBanner({ allergies }: { allergies: string[] }): ReactNode {
  return allergies.length ? (
    <div role="alert" className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3.5">
      <AlertTriangle className="mt-0.5 size-5 shrink-0 text-red-600" aria-hidden />
      <div>
        <p className="text-sm font-semibold text-red-800">Allergies</p>
        <p className="text-sm text-red-700">{allergies.join(', ')}</p>
      </div>
    </div>
  ) : (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 text-sm text-slate-600">No allergies recorded.</div>
  );
}

function Item({ label, value }: { label: string; value: string | null }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="whitespace-pre-wrap text-slate-900">{value || '—'}</dd>
    </div>
  );
}
